import { useState } from 'react'
import type { FC } from 'react'

import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import AccordionBase, * as Accordion from '@segment/matcha/recipes/Accordion'

import LessonCard from 'views/Academy/components/LessonCard'

import LabelText from 'views/Academy/components/LabelText'

import type { Course } from '@segment/api/routes/academy/types'

interface CourseSectionProps {
  course: Course
}

const copy = {
  listHeading: `In this course you’ll learn:`,
  accordionTitle: `All lessons in this course`,
}

const CourseSection: FC<CourseSectionProps> = ({ course }) => {
  const { title, courseNumber, lessons, courseIcon } = course

  const [accordionOpen, setAccordionOpen] = useState(false)

  const toggleAccordion = () => setAccordionOpen(!accordionOpen)

  return (
    <StyledSection variant="2Col" sticky={true}>
      <Section.Copy alignSelf="flex-start">
        <Section.Sticky>
          <CourseIconWrapper>
            <CourseIcon src={courseIcon.src} width="29" height="29" alt={courseIcon.title} />
          </CourseIconWrapper>
          <CopyTextBase>
            <CopyText.Heading tag="h3" variant="h2">
              {title}
            </CopyText.Heading>
            <LabelText courseNumber={courseNumber}>{lessons.length} Lessons</LabelText>
            <CopyText.Description variant="pMedium">
              <CopyText.Heading tag="div" variant="h4">
                {copy.listHeading}
              </CopyText.Heading>
              <List>
                {lessons.map((lesson) => (
                  <ListItem key={lesson.lessonNumber}>{lesson.title}</ListItem>
                ))}
              </List>
            </CopyText.Description>
          </CopyTextBase>
        </Section.Sticky>
      </Section.Copy>
      <Section.Contents data-testid="available-courses">
        <LessonCard lesson={lessons[0]} />
        <AccordionBase>
          <Accordion.Header onClick={toggleAccordion}>
            <Accordion.TitleWrapper>
              <Accordion.Title>{copy.accordionTitle}</Accordion.Title>
            </Accordion.TitleWrapper>
            <Accordion.Icon opened={accordionOpen} />
          </Accordion.Header>
          <Accordion.Body>
            {accordionOpen && (
              <StyledContentWrapper>
                <CardList>
                  {lessons.slice(1).map((lesson) => (
                    <LessonCard key={lesson.lessonNumber} lesson={lesson} />
                  ))}
                </CardList>
              </StyledContentWrapper>
            )}
          </Accordion.Body>
        </AccordionBase>
      </Section.Contents>
    </StyledSection>
  )
}

export default CourseSection

const StyledSection = styled(SectionBase)`
  ${Section.Copy} {
    position: relative;
    height: 100%;
  }

  ${Section.Sticky} {
    top: 144px;
  }
`

const CourseIconWrapper = styled.div`
  margin-bottom: 8px;
  display: block;
  width: 29px;
  height: 29px;
`

const CourseIcon = styled(Image)`
  width: 29px;
  height: 29px;
`

const List = styled.ul`
  padding-left: 24px;
`

const ListItem = styled.li``

const CardList = styled.div`
  display: grid;
  grid-auto-flow: row;
  gap: 32px;
`

const StyledContentWrapper = styled(Accordion.ContentWrapper)`
  overflow: visible;
`
